import { create } from 'zustand'
import { useMemo } from 'react'
import { apiFetch } from '../services/api'

interface FlagDetail {
  closesAt: string | null
}

interface FlagState {
  flags: Record<string, boolean>
  flagDetails: Record<string, FlagDetail>
  role: string
  loaded: boolean
  fetchFlags: () => Promise<void>
}

/**
 * Mirror the `theme_picker` flag into localStorage so the index.html pre-paint
 * script can decide whether to restore a saved theme BEFORE React (and this
 * store) load — without that mirror it would have to fetch, causing a flash.
 * Stored as '1'/'0' under `cf:flag:theme-picker`. Never throws.
 */
function mirrorThemePickerFlag(flags: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(
      'cf:flag:theme-picker',
      flags['theme_picker'] ? '1' : '0',
    )
  } catch {
    /* private mode / disabled storage — pre-paint falls back to midnight */
  }
}

export const useFlagStore = create<FlagState>((set) => ({
  flags: {},
  flagDetails: {},
  role: 'user',
  loaded: false,

  fetchFlags: async () => {
    try {
      const res = await apiFetch('/flags')
      if (!res.ok) {
        // Mark loaded even without data — gates waiting on `loaded`
        // (e.g. ReadingPage auto-draw) must settle, not hang forever.
        set({ loaded: true })
        return
      }
      const data = await res.json()
      // Mirror theme_picker for the next load's pre-paint script (see fn doc).
      mirrorThemePickerFlag(data.flags ?? {})
      set({
        flags: data.flags,
        flagDetails: data.flagDetails ?? {},
        role: data.role,
        loaded: true,
      })
    } catch {
      set({ loaded: true })
    }
  },
}))

/** Check if a specific feature flag is active for the current user */
export function useFeatureFlag(flagName: string): boolean {
  const { flags, loaded } = useFlagStore()
  if (!loaded) return false
  return flags[flagName] ?? false
}

/** Get { enabled, closesAt } for a flag — closesAt is a Date or null */
export function useFeatureFlagDetails(
  flagName: string,
): { enabled: boolean; closesAt: Date | null; loaded: boolean } {
  const { flags, flagDetails, loaded } = useFlagStore()
  const enabled = !!flags[flagName]
  const closesAtIso = flagDetails[flagName]?.closesAt ?? null
  const closesAt = useMemo(
    () => (closesAtIso ? new Date(closesAtIso) : null),
    [closesAtIso],
  )
  return { enabled, closesAt, loaded }
}

/** Get the current user's role */
export function useUserRole(): string {
  return useFlagStore((s) => s.role)
}

/** Check if the user is an admin */
export function useIsAdmin(): boolean {
  return useFlagStore((s) => s.role === 'admin')
}
